import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import SearchBox from '@/components/base/SearchBox';

interface NavItem {
  label: string;
  to: string;
  children?: { label: string; to: string }[];
}

const navItems: NavItem[] = [
  { label: '首页', to: '/' },
  {
    label: '影视分类',
    to: '/category',
    children: [
      { label: '全部影视', to: '/category' },
      { label: '电视剧', to: '/category?type=tv' },
      { label: '电影', to: '/category?type=movie' },
    ],
  },
  { label: '资讯专题', to: '/news' },
  { label: '关于我们', to: '/about' },
];

const hotSearches = ['长安十二辰', '雾都黎明', '治愈系', '星轨之外'];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
      isActive ? 'text-primary-600' : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden border-b border-primary-100 bg-primary-50 md:block">
        <div className="flex h-9 items-center justify-between gap-4 px-4 text-xs text-foreground-600 md:px-8 lg:px-12">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="flex items-center gap-1 whitespace-nowrap font-medium text-primary-600">
              <i className="ri-fire-line" aria-hidden="true"></i>
              热门搜索
            </span>
            {hotSearches.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full px-2 py-0.5 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                {tag}
              </Link>
            ))}
          </div>
          <span className="hidden whitespace-nowrap text-foreground-500 lg:block">
            演示站点 · 演员与评分均为虚构示例
          </span>
        </div>
      </div>

      <div
        className={`border-b transition-colors ${
          scrolled
            ? 'border-background-200 bg-background-50/95 backdrop-blur'
            : 'border-transparent bg-background-50/80 backdrop-blur'
        }`}
      >
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-8 lg:px-12">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="粉色水蜜桃首页">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-background-50">
              <i className="ri-plant-line text-xl" aria-hidden="true"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold tracking-tight text-foreground-950">
                粉色水蜜桃
              </span>
              <span className="block text-[10px] font-medium tracking-[0.25em] text-primary-600">
                PEACH MEDIA
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.to} className="group relative">
                  <NavLink to={item.to} className={navClass}>
                    <span className="flex items-center gap-1">
                      {item.label}
                      <i
                        className="ri-arrow-down-s-line text-base transition-transform group-hover:rotate-180"
                        aria-hidden="true"
                      ></i>
                    </span>
                  </NavLink>
                  <div className="invisible absolute left-0 top-full w-44 translate-y-1 rounded-lg border border-background-200 bg-background-50 p-1.5 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {item.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={navClass}
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <SearchBox variant="header" />
            <Link
              to="/search"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden cursor-pointer"
              aria-label="打开菜单"
            >
              <i className="ri-menu-line text-xl" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute right-0 top-0 flex h-full w-[80%] max-w-[320px] flex-col bg-background-50 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">粉色水蜜桃</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-200 text-foreground-700 cursor-pointer"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <SearchBox variant="page" />
            <nav className="mt-5 flex flex-col gap-1" aria-label="移动端导航">
              {navItems.map((item) => (
                <div key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-foreground-800 hover:bg-background-100'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.children ? (
                    <div className="ml-3 flex flex-col border-l border-background-200 pl-3">
                      {item.children.slice(1).map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="rounded-md px-2 py-2 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
}